/**
 * Utility to safely open URLs in new tabs with debugging
 * This addresses browser popup blocker issues and gesture requirements
 */

/**
 * Method 1: Direct window.open() with full URL construction
 * Most reliable when called synchronously in a click handler
 */
export const openInNewTabSafe = (path, debugLabel = "Tab Open") => {
  try {
    // Build the full URL
    const fullUrl = `${window.location.origin}${path}`;

    // Try to open - this MUST be synchronous in the click handler
    const newTab = window.open(fullUrl, "_blank", "noopener,noreferrer");

    if (!newTab) {
      console.error(`[${debugLabel}] ❌ POPUP BLOCKED`);
      return { success: false, error: "popup_blocked" };
    }

    // Try to focus
    try {
      newTab.focus();
    } catch (e) {
      // Focus failed, but tab opened successfully
    }

    return { success: true, tab: newTab };

  } catch (error) {
    console.error(`[${debugLabel}] ERROR:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Method 2: Using anchor element as fallback
 * Sometimes works when window.open() is blocked
 */
export const openInNewTabViaAnchor = (path, debugLabel = "Tab Open Anchor") => {
  try {
    const fullUrl = `${window.location.origin}${path}`;

    // Create temporary anchor element
    const anchor = document.createElement('a');
    anchor.href = fullUrl;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';

    // Add to DOM (required for some browsers)
    document.body.appendChild(anchor);

    // Click it
    anchor.click();

    // Remove from DOM
    document.body.removeChild(anchor);

    return { success: true };

  } catch (error) {
    console.error(`[${debugLabel}] ❌ ERROR:`, error);
    return { success: false, error: error.message };
  }
};

/**
 * Method 3: Browser popup blocker detection
 */
export const checkPopupBlockerStatus = () => {
  try {
    const test = window.open('', '', 'width=0,height=0');
    if (test) {
      test.close();
      return true;
    } else {
      return false;
    }
  } catch (e) {
    return null;
  }
};

/**
 * Method 4: Comprehensive fallback with both methods
 * Try window.open first, then anchor if it fails
 */
export const openInNewTabWithFallback = (path, debugLabel = "Tab Open Fallback") => {
  // Try method 1: window.open
  const result1 = openInNewTabSafe(path, `${debugLabel} [Method 1]`);
  if (result1.success) return result1;

  // Try method 2: anchor element
  const result2 = openInNewTabViaAnchor(path, `${debugLabel} [Method 2]`);
  if (result2.success) return result2;

  return { success: false, error: "all_methods_failed" };
};
